/**
 * MarketingHub.jsx — Unified Marketing Hub (Ringy-parity layout, CIVIX dark theme)
 *
 * Tabs: DRIP BUILDER · DISPOSITIONS ·
 *       KEYWORDS · PRESET REPLIES · VOICEMAILS · WEBHOOKS
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import TopBar from '../components/TopBar.jsx';
import { apiGet, apiPost, apiPut, apiPatch, apiDel } from '../lib/api.js';
import {
  MessageSquare, Zap, Bot, Tag, KeyRound, MessageCircleReply,
  Mic, Webhook, Plus, Trash2, Edit2, Save, X, CheckCircle,
  Sparkles, Clock, CalendarHeart, ShieldAlert, ChevronRight,
  CircleDot, LayoutGrid, Play, Pause, Globe, AlertTriangle,
  Upload, BarChart2, ArrowRight, RefreshCw, Phone, Library,
} from 'lucide-react';
import StarterKitsTab from './marketing/tabs/StarterKitsTab.jsx';
import MyCustomDrips from './marketing/tabs/MyCustomDrips.jsx';
import DispositionsPanel from './marketing/tabs/DispositionsPanel.jsx';
import PresetRepliesPanel from './marketing/tabs/PresetRepliesPanel.jsx';

// ── Tab definitions ────────────────────────────────────────────────────────────
// The former "Template Library" and "Drip Marketing" tabs are merged into a
// single "Drip Builder" tab (starter kits + the tenant's drip flows). The
// individual-template gallery that used to sit below the kits was dropped.
const TABS = [
  { id: 'drip-builder',   label: 'DRIP BUILDER',    icon: Zap },
  { id: 'dispositions',   label: 'DISPOSITIONS',    icon: Tag },
  { id: 'keywords',       label: 'KEYWORDS',        icon: KeyRound },
  { id: 'preset-replies', label: 'PRESET REPLIES',  icon: MessageCircleReply },
  { id: 'voicemails',     label: 'VOICEMAILS',      icon: Mic },
  { id: 'webhooks',       label: 'WEBHOOKS',        icon: Webhook },
];

// Legacy ?tab= values that should resolve to the consolidated Drip Builder tab.
const LEGACY_DRIP_TABS = new Set(['library', 'drip-marketing']);
const VALID_TABS = new Set(TABS.map((t) => t.id));

// ── Main component ─────────────────────────────────────────────────────────────
export default function MarketingHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // Legacy tab ids (library, drip-marketing) and the default both land on the
  // consolidated Drip Builder tab.
  const initialTab = LEGACY_DRIP_TABS.has(rawTab) || !VALID_TABS.has(rawTab)
    ? 'drip-builder'
    : rawTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  const { data: summaryData } = useQuery({
    queryKey: ['marketing-summary'],
    queryFn: () => apiGet('/marketing/summary'),
    staleTime: 30_000,
  });
  const summary = summaryData?.summary || {};

  function switchTab(id) {
    setActiveTab(id);
    setSearchParams({ tab: id }, { replace: true });
  }

  return (
    <div className="space-y-4 min-h-0">
      <TopBar title="Marketing Hub" subtitle="Campaigns · Automations · Messaging" />

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Drip Flows', value: summary.drips?.total ?? '—' },
          { label: 'Dispositions', value: summary.dispositionTags ?? '—' },
          { label: 'Keywords', value: '—' },
          { label: 'Preset Replies', value: summary.presetReplies ?? '—' },
          { label: 'Voicemails', value: summary.voicemails ?? '—' },
          { label: 'Webhooks', value: summary.webhooks ?? '—' },
        ].map((s) => (
          <div key={s.label} className="panel p-3 text-center">
            <div className="text-xl font-bold gradient-text">{s.value}</div>
            <div className="text-[10px] text-white/50 uppercase tracking-wide mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="panel p-1 flex gap-0.5 overflow-x-auto flex-nowrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`px-3 py-2.5 rounded-lg text-xs font-semibold tracking-wide flex items-center gap-1.5 whitespace-nowrap transition-all ${
                activeTab === t.id
                  ? 'bg-civix-purple/25 text-white border border-civix-purple/50 shadow-glow-purple'
                  : 'text-white/50 hover:text-white hover:bg-civix-panelLight'
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="min-h-0">
        {activeTab === 'drip-builder'   && <DripBuilderTab />}
        {activeTab === 'dispositions'   && <DispositionsPanel />}
        {activeTab === 'keywords'       && <KeywordsTab />}
        {activeTab === 'preset-replies' && <PresetRepliesPanel />}
        {activeTab === 'voicemails'     && <VoicemailsTab />}
        {activeTab === 'webhooks'       && <WebhooksTab />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: DRIP BUILDER (consolidated — Starter Kits + tenant drip flows)
// ─────────────────────────────────────────────────────────────────────────────
// Drips tab, two tiers: Starter Drips (preview + clone) up top, the agency's own
// My Custom Drips below. Both open the drip editor at /marketing-hub/drip-builder. Replaces the former Template Library + Drip
// Marketing tabs; the individual-template gallery was removed.
function DripBuilderTab() {
  return (
    <div className="space-y-6" data-testid="drip-builder-tab">
      <StarterKitsTab />
      <MyCustomDrips />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 5: KEYWORDS
// ─────────────────────────────────────────────────────────────────────────────
function KeywordsTab() {
  const [list, setList] = useState([
    { id: 1, keyword: 'YES', method: 'is_exactly', action: 'Mark interested', ai: false },
    { id: 2, keyword: 'STOP', method: 'is_exactly', action: 'Opt-out', ai: false },
    { id: 3, keyword: 'INFO', method: 'is_exactly', action: 'Send info pack', ai: false },
    { id: 4, keyword: 'price', method: 'contains', action: 'Send pricing card', ai: true },
    { id: 5, keyword: 'HELP', method: 'is_exactly', action: 'Send support info', ai: false },
  ]);
  const [draft, setDraft] = useState({ keyword: '', method: 'is_exactly', action: '', ai: false });

  function add() {
    if (!draft.keyword.trim()) return;
    setList([...list, { ...draft, id: Date.now() }]);
    setDraft({ keyword: '', method: 'is_exactly', action: '', ai: false });
    toast.success('Keyword saved');
  }

  const METHODS = [
    { value: 'is_exactly', label: 'Is exactly' },
    { value: 'contains', label: 'Contains keyword' },
    { value: 'starts_with', label: 'Starts with keyword' },
    { value: 'ends_with', label: 'Ends with keyword' },
  ];

  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-4">
      <div className="panel p-5 space-y-4">
        <div>
          <div className="font-display font-bold text-xl flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-civix-purple" /> Keywords
          </div>
          <p className="text-sm text-white/55 mt-1">
            CIVIX auto-detects specific words or phrases in inbound texts and triggers the matching action.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="text-white/40 text-xs uppercase">
            <tr>
              <th className="text-left py-2 pr-3">Keyword</th>
              <th className="text-left pr-3">Match</th>
              <th className="text-left pr-3">Action</th>
              <th className="pr-3">AI</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.map((k) => (
              <tr key={k.id} className="border-t border-civix-border/40">
                <td className="py-2.5 pr-3 font-mono text-civix-cyan">{k.keyword}</td>
                <td className="pr-3 text-white/70">{METHODS.find((m) => m.value === k.method)?.label || k.method}</td>
                <td className="pr-3 text-white/70">{k.action}</td>
                <td className="pr-3">
                  {k.ai ? <span className="pill-purple text-[10px] px-2 py-0.5">AI</span> : <span className="text-white/30">—</span>}
                </td>
                <td>
                  <button onClick={() => setList(list.filter((x) => x.id !== k.id))} className="text-white/30 hover:text-rose-400 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel p-5 space-y-3">
        <div className="font-display font-bold">Add keyword</div>
        <input className="input text-sm" placeholder="Keyword (e.g. STOP, INFO, price)"
          value={draft.keyword} onChange={(e) => setDraft({ ...draft, keyword: e.target.value })} />
        <select className="input text-sm" value={draft.method}
          onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
          {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input className="input text-sm" placeholder="Automated action (optional)"
          value={draft.action} onChange={(e) => setDraft({ ...draft, action: e.target.value })} />
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" className="accent-civix-purple" checked={draft.ai}
            onChange={(e) => setDraft({ ...draft, ai: e.target.checked })} />
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-civix-cyan" /> Enable AI semantic match
          </span>
        </label>
        <button className="btn-primary w-full text-sm" onClick={add}>
          Save keyword
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 7: VOICEMAILS
// ─────────────────────────────────────────────────────────────────────────────
function VoicemailsTab() {
  const qc = useQueryClient();
  const fileRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const recordedChunksRef = React.useRef([]);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['voicemail-recordings'],
    queryFn: () => apiGet('/voicemail?kind=drop'),
  });
  const recordings = data?.items || [];

  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  async function createRecording({ name, audioUrl, durationSeconds }) {
    await apiPost('/voicemail', { name, kind: 'drop', audioUrl, durationSeconds });
    qc.invalidateQueries({ queryKey: ['voicemail-recordings'] });
    qc.invalidateQueries({ queryKey: ['marketing-summary'] });
  }

  // Hotfix (May 2026): voicemail uploads previously stored the audio as a
  // base64 data: URL on the VoicemailRecording.audioUrl field. That bloats
  // Mongo and Twilio <Play> cannot use data: URLs, so the flow never worked
  // end-to-end. Temporarily disabled here while the S3/R2 upload path is
  // built. The buttons stay visible (greyed) so the feature is discoverable.
  const VOICEMAIL_UPLOAD_DISABLED_MSG =
    'Voicemail upload via this UI is temporarily disabled — file storage migration in progress.';

  async function onFileChosen(e) {
    e.target.value = '';
    toast.error(VOICEMAIL_UPLOAD_DISABLED_MSG);
  }

  async function startRecording() {
    toast.error(VOICEMAIL_UPLOAD_DISABLED_MSG);
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
  }

  async function remove(id) {
    if (!window.confirm('Delete this recording?')) return;
    try {
      await apiDel(`/voicemail/${id}`);
      qc.invalidateQueries({ queryKey: ['voicemail-recordings'] });
      qc.invalidateQueries({ queryKey: ['marketing-summary'] });
      toast.success('Deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-xl flex items-center gap-2">
            <Mic className="w-5 h-5 text-civix-purple" /> Voicemail Drops
          </div>
          <div className="text-sm text-white/55">Pre-recorded voicemail messages for one-touch drops during calls</div>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="audio/mpeg,audio/wav,audio/mp3,audio/webm,audio/ogg"
            className="hidden"
            onChange={onFileChosen}
          />
          <button
            onClick={() => toast.error(VOICEMAIL_UPLOAD_DISABLED_MSG)}
            disabled
            title={VOICEMAIL_UPLOAD_DISABLED_MSG}
            className="btn-ghost inline-flex items-center gap-2 text-sm opacity-40 cursor-not-allowed"
          >
            <Upload className="w-4 h-4" /> Upload Recording
          </button>
          <button
            onClick={() => toast.error(VOICEMAIL_UPLOAD_DISABLED_MSG)}
            disabled
            title={VOICEMAIL_UPLOAD_DISABLED_MSG}
            className="btn-primary inline-flex items-center gap-2 text-sm opacity-40 cursor-not-allowed"
          >
            <Mic className="w-4 h-4" /> Record New
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="panel p-6 text-white/50 text-sm">Loading…</div>
      ) : recordings.length === 0 ? (
        <div className="panel p-8 border-dashed flex flex-col items-center justify-center text-center min-h-[200px]">
          <Mic className="w-10 h-10 text-civix-purple/40 mb-3" />
          <div className="text-base font-semibold mb-1">No voicemail recordings yet</div>
          <div className="text-sm text-white/50 max-w-md">
            Upload an audio file or record one now to use as a one-touch drop during calls.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {recordings.map((r) => (
            <div key={r._id} className="panel-light p-4 flex items-center gap-4">
              <Mic className="w-4 h-4 text-civix-purple shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-white/45 mt-0.5">
                  {r.durationSeconds ? `${r.durationSeconds}s` : '—'}
                  {r.isDefault ? ' · default' : ''}
                </div>
              </div>
              {r.audioUrl && (
                <audio controls src={r.audioUrl} className="h-8 max-w-[240px]" />
              )}
              <button
                onClick={() => remove(r._id)}
                className="text-white/40 hover:text-rose-400 transition"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="panel p-4 flex items-start gap-3 text-sm text-white/60">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <span>
          Voicemail drops are also available through the CIVIX Phone Panel during live calls. Recordings stored here
          are instantly available in the panel.
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 8: WEBHOOKS
// ─────────────────────────────────────────────────────────────────────────────
function WebhooksTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => apiGet('/webhooks'),
  });
  const webhooks = data?.webhooks || [];

  const [draft, setDraft] = useState({ name: '', url: '', events: ['call.completed'], type: 'call' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const ALL_EVENTS = [
    { value: 'call.completed',   label: 'Call Completed',    group: 'call' },
    { value: 'call.missed',      label: 'Call Missed',       group: 'call' },
    { value: 'call.voicemail',   label: 'Voicemail Left',    group: 'call' },
    { value: 'sms.received',     label: 'SMS Received',      group: 'text' },
    { value: 'sms.sent',         label: 'SMS Sent',          group: 'text' },
    { value: 'lead.created',     label: 'Lead Created',      group: 'lead' },
    { value: 'lead.disposed',    label: 'Lead Disposed',     group: 'lead' },
    { value: 'lead.sold',        label: 'Lead Sold',         group: 'lead' },
    { value: 'appointment.booked', label: 'Appointment Booked', group: 'appt' },
  ];

  async function create() {
    if (!draft.name.trim() || !draft.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setSaving(true);
    try {
      await apiPost('/webhooks', draft);
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      qc.invalidateQueries({ queryKey: ['marketing-summary'] });
      setDraft({ name: '', url: '', events: ['call.completed'], type: 'call' });
      toast.success('Webhook created');
    } catch {
      toast.error('Failed to create webhook');
    }
    setSaving(false);
  }

  async function testWebhook(id) {
    setTesting(id);
    try {
      const r = await apiPost(`/webhooks/${id}/test`, {});
      toast.success(`Test fired — status ${r.status}`);
    } catch {
      toast.error('Test failed');
    }
    setTesting(null);
  }

  async function remove(id) {
    try {
      await apiDel(`/webhooks/${id}`);
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      qc.invalidateQueries({ queryKey: ['marketing-summary'] });
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  }

  const demoHooks = [
    { _id: 'd1', name: 'Call Webhook', url: 'https://hooks.zapier.com/hooks/catch/123/call', events: ['call.completed', 'call.missed'], active: true, lastStatus: 200 },
    { _id: 'd2', name: 'Text Webhook', url: 'https://hooks.zapier.com/hooks/catch/123/text', events: ['sms.received'], active: true, lastStatus: 200 },
  ];
  const display = webhooks.length ? webhooks : demoHooks;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-xl flex items-center gap-2">
            <Webhook className="w-5 h-5 text-civix-purple" /> Webhooks
          </div>
          <div className="text-sm text-white/55">Push call and text events to external services in real time</div>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_400px] gap-4">
        {/* List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-white/50 text-sm">Loading…</div>
          ) : (
            display.map((h) => (
              <div key={h._id} className="panel p-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${h.active ? 'bg-emerald-400' : 'bg-white/20'}`} />
                    <span className="font-semibold text-sm">{h.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {h.lastStatus && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-sm font-mono ${h.lastStatus < 300 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {h.lastStatus}
                      </span>
                    )}
                    <button
                      onClick={() => testWebhook(h._id)}
                      disabled={testing === h._id}
                      className="btn-ghost text-xs px-2 py-1 inline-flex items-center gap-1"
                    >
                      {testing === h._id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      Test
                    </button>
                    <button onClick={() => remove(h._id)} className="text-white/30 hover:text-rose-400 transition p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="font-mono text-xs text-white/45 truncate">{h.url}</div>
                <div className="flex flex-wrap gap-1">
                  {(h.events || []).map((e) => (
                    <span key={e} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-civix-border text-white/55 font-mono">{e}</span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create form */}
        <div className="panel p-5 space-y-3 h-fit">
          <div className="font-display font-bold">Add Webhook</div>
          <input
            className="input text-sm"
            placeholder="Webhook name (e.g. Zapier CRM Sync)"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <input
            className="input text-sm font-mono"
            placeholder="https://hooks.zapier.com/…"
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          />
          <div>
            <div className="text-xs text-white/50 mb-1.5">Events to subscribe</div>
            <div className="space-y-1.5">
              {ALL_EVENTS.map((ev) => (
                <label key={ev.value} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="accent-civix-purple"
                    checked={draft.events.includes(ev.value)}
                    onChange={(e) => {
                      setDraft({
                        ...draft,
                        events: e.target.checked
                          ? [...draft.events, ev.value]
                          : draft.events.filter((x) => x !== ev.value),
                      });
                    }}
                  />
                  <span className="text-white/70">{ev.label}</span>
                  <span className="text-[10px] text-white/35 font-mono ml-auto">{ev.value}</span>
                </label>
              ))}
            </div>
          </div>
          <button
            className="btn-primary w-full text-sm inline-flex items-center justify-center gap-2"
            onClick={create}
            disabled={saving}
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Webhook
          </button>
        </div>
      </div>
    </div>
  );
}
